import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Gauge, ShieldCheck, GitCompare, TrendingDown, Tag, Gift, Sparkles, Coins, Heart, PartyPopper, Crown, Star, CalendarCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Badge } from "@/components/ui/badge";
import { useState, useRef, useEffect, useCallback, memo } from "react";
import { useComparison } from "@/contexts/ComparisonContext";
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import { useIsMobile } from "@/hooks/use-mobile";

// Remembers which image URLs have finished loading in this session so a card
// that remounts (scroll back, list re-render) paints instantly instead of
// flashing the dark placeholder again.
const loadedImageUrls = new Set<string>();

interface CarCardProps {
  title: string;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  price_thb: number;
  old_price_thb?: number | null;
  discount_price_thb?: number | null;
  discount_percent?: number | null;
  promo_event?: string | null;
  promo_active?: boolean;
  mileage_km: number;
  district: string;
  district_th?: string | null;
  image_urls: string[];
  onClick: () => void;
  onLineClick?: () => void;
  car: any;
  showCompare?: boolean;
  isTwoColumn?: boolean;
  index?: number;
  // Batch observer for mobile impression tracking
  batchObserve?: (el: HTMLElement | null, listingId: string) => void;
  batchUnobserve?: (el: HTMLElement | null) => void;
}

function CarCardComponent({
  title,
  year,
  make,
  model,
  trim,
  price_thb,
  old_price_thb,
  discount_price_thb,
  discount_percent,
  promo_event,
  promo_active,
  mileage_km,
  district,
  district_th,
  image_urls,
  onClick,
  onLineClick,
  car,
  showCompare = false,
  isTwoColumn = false,
  index = 0,
  batchObserve,
  batchUnobserve,
}: CarCardProps) {
  const { language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState<boolean>(() => {
    const first = image_urls?.[0];
    return !!first && loadedImageUrls.has(first);
  });
  const { addToComparison, removeFromComparison, isComparing, comparedCars } = useComparison();
  const isMobile = useIsMobile();

  // Use compact layout only on narrow mobile (<640px). Tablets (≥640px) get desktop-style card
  // even in two-column mode because there's enough horizontal space.
  const [isNarrow, setIsNarrow] = useState<boolean>(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 639px)").matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 639px)");
    const onChange = () => setIsNarrow(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  const useCompactLayout = isNarrow && isTwoColumn;
  
  // Use Thai district name when in Thai mode
  const displayDistrict = language === 'th' && district_th ? district_th : district;
  const isSelected = isComparing(car.id);
  const canAdd = comparedCars.length < 3;
  const cardRef = useRef<HTMLDivElement>(null);
  const hasTrackedViewRef = useRef(false);
  
  const listingId = car.id;

  // Track viewport impression (once per session per listing)
  // On mobile with batch observer: delegate to shared observer
  // On desktop: use per-card IntersectionObserver
  const useBatchTracking = isMobile && !!batchObserve;

  // Register with batch observer on mobile
  useEffect(() => {
    if (!useBatchTracking || !cardRef.current || !listingId) return;
    batchObserve!(cardRef.current, listingId);
    const el = cardRef.current;
    return () => batchUnobserve?.(el);
  }, [useBatchTracking, listingId, batchObserve, batchUnobserve]);

  // Per-card observer for desktop only
  useEffect(() => {
    if (useBatchTracking) return; // Mobile uses batch
    if (!listingId || hasTrackedViewRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedViewRef.current) {
            hasTrackedViewRef.current = true;
            import("@/lib/analytics")
              .then(({ trackListingView }) => trackListingView(listingId))
              .catch(() => {});
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [listingId, useBatchTracking]);

  // Smart image prefetch: warm the first image as soon as card is within 800px of viewport.
  // Native lazy loading only kicks in much later, causing perceived "reload on scroll".
  const prefetchedRef = useRef(false);
  useEffect(() => {
    if (prefetchedRef.current || !cardRef.current) return;
    if (index < 4) return; // first cards already eager-loaded
    const firstUrl = image_urls?.[0];
    if (!firstUrl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !prefetchedRef.current) {
            const img = new Image();
            img.decoding = "async";
            img.src = firstUrl;
            prefetchedRef.current = true;
            observer.disconnect();
          }
        });
      },
      { rootMargin: "800px 0px" }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [index, image_urls]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US").format(price);
  };
  const formatMileage = (km: number) => {
    const thousands = Math.floor(km / 1000);
    if (thousands === 0) return 'xxx';
    return new Intl.NumberFormat("th-TH").format(thousands) + ',xxx';
  };
  const formatMileageCompact = (km: number) => {
    const thousands = Math.floor(km / 1000);
    if (thousands === 0) return '<1K';
    return `${new Intl.NumberFormat("th-TH").format(thousands)}K`;
  };

  // Get brand logo based on make - some logos need size adjustments
  const getLogoScale = () => {
    const scaledBrands: Record<string, string> = {
      'mini': 'scale-125',
      'audi': 'scale-110',
    };
    return scaledBrands[make.toLowerCase()] || '';
  };

  const getCarLogoBadge = (compact?: boolean) => {
    return <div className={compact ? "h-6 w-6 flex items-center justify-center flex-shrink-0" : "h-8 w-8 flex items-center justify-center flex-shrink-0"}>
        <img 
          src={`/logos/${make.toLowerCase()}.png?v=3`} 
          alt={`${make} logo — Premium ${make} used cars`} 
          className={cn("h-full w-full object-contain", getLogoScale())} 
          loading="lazy"
          fetchPriority="low"
          width={compact ? 24 : 32}
          height={compact ? 24 : 32}
          decoding="async"
          onError={e => {
            e.currentTarget.style.display = 'none';
          }} 
        />
      </div>;
  };
  const displayImages = image_urls.length > 0 ? image_urls : ["/placeholder.svg"];

  // Embla carousel for image swiping - disabled for single-image cards to reduce mobile work.
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    active: displayImages.length > 1,
    loop: false, 
    dragFree: false, 
    align: 'start',
    containScroll: 'trimSnaps',
    skipSnaps: false
  });
  
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentImageIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Get promo icon and color based on promo_event type
  const getPromoStyle = (promoEvent: string) => {
    const promoMap: Record<string, {
      Icon: any;
      bgColor: string;
    }> = {
      "Christmas Special": {
        Icon: Gift,
        bgColor: "bg-red-500/90"
      },
      "New Year Special": {
        Icon: Sparkles,
        bgColor: "bg-purple-500/90"
      },
      "Chinese New Year Special": {
        Icon: Coins,
        bgColor: "bg-red-600/90"
      },
      "Valentine Special": {
        Icon: Heart,
        bgColor: "bg-pink-500/90"
      },
      "Year-End Special": {
        Icon: PartyPopper,
        bgColor: "bg-amber-500/90"
      },
      "Premium Highlight": {
        Icon: Crown,
        bgColor: "bg-yellow-500/90"
      },
      "Price Drop": {
        Icon: TrendingDown,
        bgColor: "bg-green-500/90"
      },
      "Limited Series": {
        Icon: Star,
        bgColor: "bg-blue-500/90"
      }
    };
    return promoMap[promoEvent] || {
      Icon: Tag,
      bgColor: "bg-primary/30"
    };
  };

  // Determine promo and verification status
  const hasPromo = promo_event && promo_event !== "None";
  const isMotorVerified = car.motorverified || car.service_proof_url;

  const handleCompareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSelected) {
      removeFromComparison(car.id);
    } else if (canAdd) {
      addToComparison(car);
    }
  };
  const handleCardClick = () => {
    // Only block click if emblaApi exists and clickAllowed explicitly returns false (drag occurred)
    const clickAllowed = emblaApi ? (emblaApi as any).clickAllowed?.() : true;
    if (clickAllowed === false) return;
    onClick();
  };
  // Generate SEO-friendly price string
  const seoPrice = new Intl.NumberFormat("th-TH").format(price_thb);
  const seoMileage = new Intl.NumberFormat("th-TH").format(mileage_km);

  const scrollPrev = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    emblaApi?.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    emblaApi?.scrollNext();
  }, [emblaApi]);

  return <Card ref={cardRef} className="group cursor-pointer overflow-hidden rounded-[24px] border-border bg-card backdrop-blur-sm shadow-sm transition-shadow duration-300 hover:border-border/70 hover:shadow-2xl hover:shadow-primary/20" onClick={handleCardClick}>
      {/* Hidden SEO content for crawlers - covers มือสอง + ราคา search patterns */}
      <div className="sr-only" aria-hidden="false">
        <h3>{make} {model} มือสอง {year}</h3>
        <h4>{make} ราคา - {make} {model} ราคา {seoPrice} บาท</h4>
        <p>
          ขาย{make} {model} มือสอง ปี{year} ราคา {seoPrice} บาท 
          เลขไมล์ {seoMileage} กม. {district} กรุงเทพ 
          รถมือสอง{make} รถยนต์มือสอง {make} {model} 
          {trim && `${trim} มือสอง`} สภาพดี รถบ้าน รถมือสองราคาถูก
          รถ{make}มือสอง ซื้อรถมือสอง {model} ขายรถมือสอง |
          {make} ราคา | {make} {model} ราคา | {make} ราคาเท่าไหร่ |
          {make} มือสอง ราคา | {make} {model} มือสอง ราคา |
          รถ{make} ราคา | ราคา{make} {model} | {make} ราคา {seoPrice} บาท |
          {make} {model} ราคากี่บาท | {make} มือสอง ราคาถูก |
          เช็คราคา{make} | ราคารถ{make}มือสอง
        </p>
      </div>
      
      <div 
        className="relative aspect-[4/3] bg-muted rounded-t-[24px] overflow-hidden"
      >
        {/* Image Carousel - No peeking */}
        <div className="embla h-full w-full overflow-hidden select-none cursor-grab active:cursor-grabbing" ref={emblaRef} style={{ touchAction: 'manipulation' }}>
          <div className="embla__container flex h-full">
            {displayImages.map((url, imgIndex) => (
              <figure key={imgIndex} className="relative flex h-full min-w-0 items-center justify-center overflow-hidden bg-muted" style={{ flex: '0 0 100%' }}>
                {/* Only render images that are visible or adjacent for smooth swiping */}
                {Math.abs(imgIndex - currentImageIndex) <= 1 ? (
                  <>
                    {/* Blur placeholder while loading */}
                    {!imageLoaded && imgIndex === 0 && (
                      <div className="absolute inset-0 bg-muted animate-pulse" />
                    )}
                    <img 
                      src={url} 
                      alt={`${year} ${make} ${model} มือสอง ${district} - รถมือสอง${make} ${trim || ''} TransShift รถยนต์มือสอง`} 
                      className={cn(
                        "h-full w-full object-cover object-center transition-opacity duration-300 select-none",
                        !imageLoaded && imgIndex === 0 ? "opacity-0" : "opacity-100"
                      )}
                      draggable={false}
                      // First 4 cards eager load, rest lazy; first 2 high priority
                      loading={index < 4 ? "eager" : "lazy"}
                      fetchPriority={index < 2 ? "high" : "auto"}
                      decoding="async"
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 400px"
                      width={400}
                      height={300}
                      ref={(el) => {
                        // Already decoded by the browser (e.g. scrolled back): show instantly.
                        if (el && el.complete && el.naturalWidth > 0 && imgIndex === 0) {
                          loadedImageUrls.add(url);
                          setImageLoaded(true);
                        }
                      }}
                      onLoad={() => {
                        loadedImageUrls.add(url);
                        if (imgIndex === 0) setImageLoaded(true);
                      }}
                      onError={e => {
                        const target = e.target as HTMLImageElement;
                        // Try next image in array, otherwise use placeholder
                        const nextIndex = imgIndex + 1;
                        if (displayImages[nextIndex] && target.src !== displayImages[nextIndex]) {
                          target.src = displayImages[nextIndex];
                        } else {
                          target.src = "/placeholder.svg";
                        }
                        setImageLoaded(true);
                      }}
                    />
                    {/* Semantic figcaption for Image Search SEO */}
                    <figcaption className="sr-only">
                      {make} {model} มือสอง ปี{year} ราคา {seoPrice} บาท - TransShift
                    </figcaption>
                  </>
                ) : (
                  // Placeholder for non-adjacent images to maintain layout
                  <div className="h-full w-full bg-muted" />
                )}
              </figure>
            ))}
          </div>
        </div>
        
        
        {/* Promo Icon Overlay - Top Right (trust badge moved to info section) */}
        {hasPromo && <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
            {/* Promo Tag Icon - Gray (no color on cards) */}
            {(() => {
              const { Icon } = getPromoStyle(promo_event!);
              return <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/60 backdrop-blur-sm" title={promo_event} aria-label={promo_event}>
                  <Icon className="h-5 w-5 text-white" />
                </div>;
            })()}
          </div>}
        
        {showCompare && <div className={cn("absolute left-3", hasPromo ? "top-12" : "top-3")}>
            <button onClick={handleCompareClick} className={cn("flex h-10 w-10 items-center justify-center rounded-full backdrop-blur-sm transition-all", isSelected ? "bg-primary text-primary-foreground" : "bg-overlay-blue text-white hover:bg-overlay-blue-light", !canAdd && !isSelected && "opacity-50 cursor-not-allowed")} disabled={!canAdd && !isSelected}>
              <GitCompare className="h-5 w-5" />
            </button>
          </div>}

        {/* Desktop hover arrows */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={scrollPrev}
              aria-label="Previous image"
              className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/75 z-10"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={scrollNext}
              aria-label="Next image"
              className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/75 z-10"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Year Badge removed from image for compact layout - now shown with price */}
      </div>

      {/* Mobile Badges - Under Image, Above Title */}
      

      <CardContent className={cn("p-3 sm:p-4", useCompactLayout ? "space-y-2" : "space-y-3")}>
        {useCompactLayout ? <>
            {/* Two-Column Layout: Vertical/Stacked */}
            {/* Header: Logo + Title */}
            <div className="flex items-center gap-2">
              {getCarLogoBadge(true)}
              <div className="min-w-0 flex-1">
                <h2 className="text-xs sm:text-sm font-semibold text-card-foreground line-clamp-1">{title}</h2>
              </div>
            </div>

            {/* Info Section - Grid layout for aligned columns */}
            <div className="grid grid-cols-2 gap-1 text-xs w-full text-muted-foreground">
              {/* Row 1: KM (left) + Year (right) */}
              <div className="flex items-center gap-1">
                <Gauge className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{formatMileage(mileage_km)} km</span>
              </div>
              <div className="flex items-center gap-1">
                <CalendarCheck className="h-3.5 w-3.5 shrink-0" />
                <span className="font-medium">{year}</span>
              </div>
              {/* Row 2: Location (left) + Price (right) */}
              <div className="flex items-center gap-1 min-w-0">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{displayDistrict}</span>
              </div>
              <div className="flex items-center gap-1 text-foreground">
                <span className="text-sm font-semibold">฿</span>
                <span className="text-xs font-bold">
                  {formatPrice(promo_active === true && discount_price_thb && discount_price_thb !== price_thb ? discount_price_thb : price_thb)}
                </span>
                {promo_active === true && discount_price_thb && discount_price_thb !== price_thb && (
                  <TrendingDown className="h-3 w-3 text-green-500" />
                )}
              </div>
            </div>

            <div className="flex gap-1.5">
              <Button variant="outline" className="flex-1 h-auto py-1.5 text-[10px] bg-background hover:bg-muted border-border text-foreground">
                {language === "en" ? "See More" : "ดูเพิ่มเติม"}
              </Button>
              {isMotorVerified && (
                <div
                  className="flex h-auto py-1.5 px-2 items-center justify-center rounded-md border border-border/40"
                  style={{
                    background: 'linear-gradient(135deg, #d8d8d8 0%, #a8a8a8 25%, #c4c4c4 50%, #989898 75%, #bcbcbc 100%)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.35)',
                    opacity: 0.85
                  }}
                  title="MotorVerified"
                  aria-label="MotorVerified"
                >
                  <ShieldCheck className="h-4 w-4" style={{ color: '#1a1a2e' }} />
                </div>
              )}
              <Button
                variant="outline"
                className="h-auto py-1.5 px-2 border-border/40 rounded-md"
                style={{
                  background: 'linear-gradient(135deg, #d8d8d8 0%, #a8a8a8 25%, #c4c4c4 50%, #989898 75%, #bcbcbc 100%)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.35)',
                  opacity: 0.85,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onLineClick?.();
                }}
                aria-label="Contact via LINE"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="#1a1a2e">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                </svg>
              </Button>
            </div>
          </> : <>
            {/* Single-Column Layout: Horizontal */}
            {/* Title */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {getCarLogoBadge()}
                <h2 className="text-lg font-semibold text-card-foreground truncate">{title}</h2>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="secondary" className="rounded-full px-3 py-1 text-base bg-muted dark:bg-secondary text-foreground dark:text-secondary-foreground font-semibold border border-border">
                  {year}
                </Badge>
                {/* Trust Badge - After Year for desktop/tablet/mobile 1-per-row */}
                {isMotorVerified && (
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-full border border-border/40"
                    style={{
                      background: 'linear-gradient(135deg, #d8d8d8 0%, #a8a8a8 25%, #c4c4c4 50%, #989898 75%, #bcbcbc 100%)',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.35)',
                      opacity: 0.8
                    }}
                    title="MotorVerified"
                    aria-label="MotorVerified"
                  >
                    <ShieldCheck className="h-[18px] w-[18px]" style={{ color: '#1a1a2e' }} />
                  </div>
                )}
              </div>
            </div>

            {/* Info Section - Horizontal */}
            <div className="flex items-center justify-between gap-2 text-muted-foreground">
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <Gauge className="h-4 w-4 shrink-0" />
                <span className="text-base truncate">{formatMileage(mileage_km)} km</span>
              </div>
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <MapPin className="h-4 w-4 shrink-0" />
                <span className="text-base truncate">{displayDistrict}</span>
              </div>
              <div className="flex items-baseline gap-1.5 shrink-0 text-foreground">
                <span className="text-base">฿</span>
                <span className="text-lg font-bold">
                  {formatPrice(promo_active === true && discount_price_thb && discount_price_thb !== price_thb ? discount_price_thb : price_thb)}
                </span>
                {promo_active === true && discount_price_thb && discount_price_thb !== price_thb && (
                  <TrendingDown className="h-4 w-4 text-green-500" />
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 h-auto py-1.5 text-sm bg-background hover:bg-muted border-border text-foreground">
                {language === "en" ? "See More" : "ดูเพิ่มเติม"}
              </Button>
              <Button
                variant="outline"
                className="h-auto py-1.5 px-3 border-border/40 rounded-md"
                style={{
                  background: 'linear-gradient(135deg, #d8d8d8 0%, #a8a8a8 25%, #c4c4c4 50%, #989898 75%, #bcbcbc 100%)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.35)',
                  opacity: 0.85,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onLineClick?.();
                }}
                aria-label="Contact via LINE"
              >
                <svg viewBox="0 0 24 24" className="h-6 w-6" fill="#1a1a2e">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                </svg>
              </Button>
            </div>
          </>}
      </CardContent>
    </Card>;
}

// Re-render only when the actual listing data or view flags change. Without this
// every scroll-driven state update on the page re-rendered the whole grid, which
// caused the visible flicker when scrolling back up.
export const CarCard = memo(CarCardComponent, (prev, next) => {
  return (
    prev.car?.id === next.car?.id &&
    prev.title === next.title &&
    prev.price_thb === next.price_thb &&
    prev.discount_price_thb === next.discount_price_thb &&
    prev.promo_active === next.promo_active &&
    prev.mileage_km === next.mileage_km &&
    prev.district === next.district &&
    prev.district_th === next.district_th &&
    prev.showCompare === next.showCompare &&
    prev.isTwoColumn === next.isTwoColumn &&
    prev.index === next.index &&
    prev.image_urls?.[0] === next.image_urls?.[0] &&
    prev.image_urls?.length === next.image_urls?.length
  );
});